import { Component, EventEmitter, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderService } from '../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { BaseComponent } from '../../core/base.component';
import { AuthService } from '../../core/security/auth.service';
import { RolesEnum } from '../../core/security/roles.enum';
import { BulkEditComponent } from '../bulk-edit/bulk-edit.component';
import { NotificationService } from '../../notification/notification.service';
import { PhoneNumberGridComponent } from './phone-number-grid.component';
import { MatDialog } from '@angular/material/dialog';
import { FilterOptionModel } from '../../dialogs/grid-filter/filter-option.model';
import { MatAlertHtmlDialogComponent } from '../../dialogs/mat-alert-html/mat-alert-dialog.component';
import { SelectedFilterValuesModel } from '../../dialogs/grid-filter/selected-filter-values.model';
import { MatExportDialogComponent } from '../../dialogs/mat-export/mat-export-dialog.component';
import { PhoneNumbersService } from '../phone-numbers.service';
import { ExportFieldModel } from '../../api/models/export-field-model';
import { CurrentUser } from '../../api/models/current-user';
import { ModulesEnum } from '../../core/security/modules.enum';
import { LookupModel } from '../../api/models';
import { FlexModule } from '@angular/flex-layout';
import { GridFilterComponent } from '../../dialogs/grid-filter/grid-filter.component';
import { FilterRowAdditionalContentComponent } from '../../dialogs/grid-filter/filter-row-additional-content.component';
import { NgIf } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'app-users',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    standalone: true,
    imports: [
        FlexModule,
        GridFilterComponent,
        FilterRowAdditionalContentComponent,
        NgIf,
        MatButton,
        MatTooltip,
        MatIcon,
        PhoneNumberGridComponent,
    ],
})
export class PhoneNumbersComponent extends BaseComponent implements OnInit, OnDestroy {
  filterOptions: FilterOptionModel[] = [];
  filterChangeEvent = new EventEmitter<any>();
  selectedFilter: SelectedFilterValuesModel[];
  exportColumns: any[] = [];
  selectedExportColumns: any[] = [];
  hasExportAccess = false;
  detailsUrl: string;
  customerUsers: LookupModel[] = [];

  @ViewChild('phoneNumbersGrid', { static: true }) phoneNumbersGrid: PhoneNumberGridComponent;

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    public authService: AuthService,
    private router: Router,
    public dialog: MatDialog,
    public phoneNumbersService: PhoneNumbersService,
    private notificationService: NotificationService,
  ) {
    super(route, headerService, titleService, authService);

    this.detailsUrl = '../phone-number';

    this.editRoles = [RolesEnum.PhoneNumbers_Edit, RolesEnum.PhoneNumbers_Delete];
    this.deleteRoles = [RolesEnum.PhoneNumbers_Delete];

    this.headerService.hideSearchInput();
  }

  onCurrentUserChanged(user?: CurrentUser) {
    super.onCurrentUserChanged(user);
    if (user) {
      this.hasExportAccess =
        this.authService.userHasPackage(ModulesEnum.PhoneNumbersExportModule) &&
        this.authService.hasAccess([RolesEnum.PhoneNumbers_Export]);
    } else {
      this.hasExportAccess = false;
    }
  }

  async ngOnInit() {
    super.ngOnInit();

    this.customerUsers = (await this.phoneNumbersService.customerSmsUsersLookup())?.[0] || [];

    this.initFilterOptions();
  }

  onBulkEditClick() {
    const ids = this.phoneNumbersGrid.selectedRows.selected.map(x => x.id);

    if (ids.length === 0) {
      this.notificationService.notifyDanger('Select at least one phone number to edit');
      return;
    }

    const selectedFilter = this.phoneNumbersGrid.getDataFilter();

    const dialogRef = this.dialog.open(BulkEditComponent, {
      width: '400px',
      data: {
        ids: ids,
        cnam: '',
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result === 'success') {
        this.phoneNumbersGrid.selectedRows.clear();
        this.filterChangeEvent.emit(selectedFilter);

        this.dialog.open(MatAlertHtmlDialogComponent, {
          width: '400px',
          height: '300px',
          data: {
            title: 'Please Note',
            actionTitle: 'Close',
            html: `
            <div class="alert alert-warning">
              Caller ID should be active within 7 to 14 business days.
              If the accurate Caller ID isn't showing 14 days after the request was submitted, contact support@telego.net for
              assistance.
            </div>
            `,
          },
        });
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.headerService.hideSearchInput();
    this.headerService.searchText.next('');
  }

  onFilterChanged(filter: any) {
    this.filterChangeEvent.emit(filter);
  }

  initFilterOptions(): void {
    this.filterOptions = [
      {
        filterKey: 'searchText',
        multiValue: false,
        showAlways: true,
        type: 'string',
        title: 'Any',
      },
      {
        filterKey: 'alias',
        multiValue: false,
        showAlways: true,
        type: 'string',
        title: 'Alias',
      },
      {
        filterKey: 'phoneNumber',
        multiValue: false,
        showAlways: true,
        type: 'phoneNumber',
        title: 'Phone Number',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Status',
        displayValue: 'Active',
        value: 'Active',
        searchText: 'Status: Active',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Status',
        displayValue: 'Available',
        value: 'Available',
        searchText: 'Status: Available',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Status',
        displayValue: 'On Hold',
        value: 'On Hold',
        searchText: 'Status: On Hold',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Status',
        displayValue: 'Porting In Progress',
        value: 'Porting In Progress',
        searchText: 'Status: Porting In Progress',
      },
      {
        filterKey: 'types',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Type',
        displayValue: 'Phone Number',
        value: 'Phone Number',
        searchText: 'Type: Phone Number',
      },
      {
        filterKey: 'types',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Type',
        displayValue: 'Fax Number',
        value: 'Fax Number',
        searchText: 'Type: Fax Number',
      },
      {
        filterKey: 'features',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Feature',
        displayValue: 'Fax Over Portal',
        value: 'fax',
        searchText: 'Feature: Fax Over Portal',
      },
      {
        filterKey: 'features',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Feature',
        displayValue: 'Messaging',
        value: 'sms',
        searchText: 'Feature: Messaging',
      },
      {
        filterKey: 'features',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Feature',
        displayValue: 'Business WhatsApp',
        value: 'whatsapp',
        searchText: 'Feature: Business WhatsApp',
      },
      {
        filterKey: 'activeFeature',
        multiValue: false,
        showAlways: true,
        type: 'static',
        title: 'Active Feature',
        displayValue: 'On',
        value: 'on',
        searchText: 'Active Feature: On',
      },
      {
        filterKey: 'activeFeature',
        multiValue: false,
        showAlways: true,
        type: 'static',
        title: 'Active Feature',
        displayValue: 'Off',
        value: 'off',
        searchText: 'Active Feature: Off',
      },
    ];

    if (this.customerUsers.length) {
      for (const item of this.customerUsers) {
        this.filterOptions.push({
          filterKey: 'assignedTo',
          multiValue: false,
          showAlways: false,
          type: 'static',
          title: 'Assigned To',
          displayValue: item.text,
          value: item.id,
          searchText: `Assigned To: ${item.text}`,
        });
      }
    }
  }

  openExportDialog() {
    this.exportColumns = this.phoneNumbersService.exportColumns.map((x: any) => {
      return {
        text: x.title,
        value: x.id,
        field: x.field,
        hidden: x.hidden,
        id: x.id,
      };
    });

    this.selectedExportColumns = this.exportColumns
      .filter(item => {
        return !item.hidden;
      })
      .map(item => item.id);

    const dialogRef = this.dialog.open(MatExportDialogComponent, {
      width: '500px',
      height: '60%',
      data: {
        availableColumns: this.exportColumns,
        selectedColumns: this.selectedExportColumns,
      },
    });

    dialogRef.afterClosed().subscribe(async (result: any[]) => {
      if (result) {
        this.selectedExportColumns = result;
        await this.onExport();
      }
    });
  }

  async onExport() {
    const exColumns: ExportFieldModel[] = this.phoneNumbersService.exportColumns.filter(x => {
      return this.selectedExportColumns.indexOf(x.id) > -1;
    });

    await this.phoneNumbersService.exportPhoneNumbers({
      filter: this.phoneNumbersGrid.getDataFilter(),
      exportFields: exColumns,
    });
  }
}
